import { useSnackbar } from 'notistack';
import React, { memo, useContext, useEffect, useReducer } from 'react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { WebsiteDesignType } from '../../../catalog/enums/WebsiteDesignType';
import { isValidObject, isValidString } from '../../../catalog/Validity';
import { context } from '../../../globalState/GlobalState';
import { createCustomTitleTag, createCustomDescriptionTag, createCustomKeywordsTag } from '../uiElements/helmet/MetaTags';
import TitleAndDescriptionTags from '../uiElements/helmet/TitleAndDescriptionTags';
import { fetchSeasonAPI, fetchSeasonPlayersAPI, fetchSeasonCoachAPI } from '../../apiCalls/SeasonAPICalls';
import SeasonPlayersAndCoaches1 from '../../designs/seasonPlayersAndCoaches1/SeasonPlayersAndCoaches1';
import { sendWebPageAnalytics } from '../../../catalog/Analytics';

const initialState = {
    players: [],
    coaches: [],
    seasonName: '',
    mainLoadingSpinner: true,
    rosterLoadingSpinner: true,
    coachesLoadingSpinner: true,
    error: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'fetchPlayers':
            return { ...state, players: action.players };
        case 'fetchCoaches':
            return { ...state, coaches: action.coaches };
        case 'fetchSeason':
            return { ...state, seasonName: action.name };
        case 'setMainLoadingSpinner':
            return { ...state, mainLoadingSpinner: action.loadingSpinner };
        case 'setRosterLoadingSpinner':
            return { ...state, rosterLoadingSpinner: action.loadingSpinner };
        case 'setCoachesLoadingSpinner':
            return { ...state, coachesLoadingSpinner: action.loadingSpinner };
        case 'setError':
            return { ...state, error: true };
        default:
            return initialState;
    }
}

const SeasonPlayersAndCoaches = () => {
    const history = useHistory();
    const params = useParams();
    const { globalState } = useContext(context);
    const [state, setState] = useReducer(reducer, initialState);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (isValidString(state.seasonName)) {
            sendWebPageAnalytics(globalState, `Roster for Season ${state.seasonName}`);
        }
    }, [globalState, state.seasonName]);

    useEffect(() => {
        async function fetchPlayersAndCoaches() {
            if (isValidString(globalState.accountId) && isValidString(params.seasonid)) {
                setState({ type: 'setMainLoadingSpinner', loadingSpinner: true });
                setState({ type: 'setRosterLoadingSpinner', loadingSpinner: true });
                setState({ type: 'setCoachesLoadingSpinner', loadingSpinner: true });
                await fetchSeasonAPI(globalState.accountId, params.seasonid, setState, enqueueSnackbar);
                setState({ type: 'setMainLoadingSpinner', loadingSpinner: false });
                await fetchSeasonPlayersAPI(globalState.accountId, params.seasonid, setState, enqueueSnackbar);
                setState({ type: 'setRosterLoadingSpinner', loadingSpinner: false });
                await fetchSeasonCoachAPI(globalState.accountId, params.seasonid, setState, enqueueSnackbar);
                setState({ type: 'setCoachesLoadingSpinner', loadingSpinner: false });
            }
        }
        fetchPlayersAndCoaches();
    }, [history.location.pathname, globalState.accountId, params.seasonid]);

    const getPlayersAndCoachesDesign = () => {
        if (!isValidObject(globalState) || !isValidObject(globalState.websiteStyles)) {
            return null;
        }

        if (globalState.websiteStyles.design.players === WebsiteDesignType.DESIGN_1) {
            return (
                <SeasonPlayersAndCoaches1
                    players={state.players}
                    coaches={state.coaches}
                    seasonName={state.seasonName}
                    mainLoadingSpinner={state.mainLoadingSpinner}
                    rosterLoadingSpinner={state.rosterLoadingSpinner}
                    coachesLoadingSpinner={state.coachesLoadingSpinner}
                />
            );
        }
        return null;
    };

    if (state.error) {
        return <Redirect to="/notfound" />;
    }

    return (
        <>
            <TitleAndDescriptionTags
                title={createCustomTitleTag(`Roster for Season ${state.seasonName}`, globalState)}
                description={createCustomDescriptionTag(`Check out our roster for Season ${state.seasonName}`, globalState)}
                keywords={createCustomKeywordsTag(`Roster, Season, Season ${state.seasonName}`, globalState)}
            />
            {getPlayersAndCoachesDesign()}
        </>
    );
};

export default memo(SeasonPlayersAndCoaches);
